import LegalLayout from "@/components/site/LegalLayout";

const SECTIONS = [
  {
    title: "1 · Who We Are",
    body: "BoT Summit (“we”, “our”, “us”) is the organising body of the BoT Summit 2026 conference held in Bengaluru, India. This Privacy Policy explains what information we collect when you register for, attend or interact with BoT Summit, how we use it, and the rights you have over it.",
  },
  {
    title: "2 · Information We Collect",
    items: [
      "Registration details: full name, work email, phone, organisation, role and selected pass type.",
      "Contact enquiries: name, email, subject and message submitted via the Contact form.",
      "Attendance data: session check-ins, badge scans at sponsor booths (only with your explicit consent).",
      "Technical data: IP address, browser user-agent and cookies necessary to operate the website and detect abuse.",
    ],
    after: "We do not intentionally collect sensitive personal data (health, religion, political views). Please do not include such data in free-text fields.",
  },
  {
    title: "3 · How We Use Your Information",
    items: [
      "To process your registration, issue your badge and send event logistics.",
      "To respond to messages submitted via the Contact form.",
      "To send occasional updates about the programme, speakers and schedule changes.",
      "To improve the summit experience through aggregate, anonymised analytics.",
      "To comply with legal obligations (e.g. tax, regulatory requests).",
    ],
  },
  {
    title: "4 · Legal Basis",
    body: "We rely on one of the following lawful bases for each use of your data: (a) your consent, (b) performance of a contract (your registration), (c) our legitimate interests in running a safe and well-organised summit, or (d) compliance with a legal obligation.",
  },
  {
    title: "5 · Sharing & Third Parties",
    body: "We do not sell your personal data. We share limited information only with:",
    items: [
      "Infrastructure providers hosting our website and database (under strict data-processing agreements).",
      "Payment processors, if you purchase a ticket (they receive the minimum data needed to process payment).",
      "Sponsors or partners only when you explicitly opt in — for example by scanning your badge at their booth.",
      "Authorities, if required by applicable law.",
    ],
  },
  {
    title: "6 · International Transfers",
    body: "Our primary servers are hosted in India. Where data is processed outside India, we rely on standard contractual clauses or equivalent safeguards recognised by applicable data-protection law.",
  },
  {
    title: "7 · Retention",
    body: "We retain registration data for up to 24 months after the summit to enable returning-delegate benefits and next-edition communications. Contact-form messages are retained for 12 months. You can request earlier deletion at any time (see §10).",
  },
  {
    title: "8 · Security",
    body: "We protect your data with TLS encryption in transit, encrypted backups, role-based access control for organising staff, and routine security reviews. While we follow industry best practice, no online service can guarantee absolute security.",
  },
  {
    title: "9 · Cookies",
    body: "The BoT Summit website uses only essential cookies required to maintain your session (e.g. admin login) and basic anonymous analytics. We do not run third-party advertising trackers on this site.",
  },
  {
    title: "10 · Your Rights",
    items: [
      "Access: request a copy of the personal data we hold about you.",
      "Correction: ask us to fix any inaccurate information.",
      "Deletion: request that we erase your data, subject to legal retention requirements.",
      "Objection / Restriction: ask us to stop or limit certain uses of your data.",
      "Portability: receive your data in a machine-readable format.",
      "Withdrawal of consent: opt out of marketing at any time via the unsubscribe link or by emailing us.",
    ],
    after: "To exercise any of these rights, write to info@botsummits.com. We will respond within 30 days.",
  },
  {
    title: "11 · Children",
    body: "BoT Summit is an industry event intended for attendees aged 18 and above. We do not knowingly collect personal data from children. If you believe a minor has submitted data, contact us and we will delete it promptly.",
  },
  {
    title: "12 · Changes to this Policy",
    body: "We may update this Policy from time to time. Material changes will be communicated to registered delegates by email and posted on this page. The “Last updated” date above always reflects the current version.",
  },
  {
    title: "13 · Contact",
    body: "Questions, concerns or requests about this Privacy Policy can be sent to:",
    items: [
      "Email: info@botsummits.com",
      "Phone / WhatsApp: +44 7915361665 · +91 8825035701",
      "Post: BoT Summit Secretariat, Bengaluru, India",
    ],
  },
];

export default function Privacy() {
  return (
    <LegalLayout
      eyebrow="/ Policy · Privacy"
      title="Privacy Policy"
      updated="December 2025"
    >
      <p className="lead text-lg text-zinc-700 leading-relaxed">
        BoT Summit takes your privacy seriously. This policy explains what personal
        data we collect when you register, how we use it, who we share it with, and
        the controls you have over it — in plain language.
      </p>

      <div className="mt-12 space-y-10">
        {SECTIONS.map((s, i) => (
          <section key={i} data-testid={`privacy-section-${i}`}>
            <h2 className="font-display text-xl md:text-2xl uppercase tracking-tight border-t border-zinc-200 pt-6">
              {s.title}
            </h2>
            {s.body && <p className="mt-4 text-zinc-700 leading-relaxed">{s.body}</p>}
            {s.items && (
              <ul className="mt-4 space-y-2">
                {s.items.map((it, k) => (
                  <li key={k} className="flex gap-3 text-zinc-700">
                    <span className="text-[#002FA7] font-mono text-sm mt-1 shrink-0">
                      0{k + 1}
                    </span>
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            )}
            {s.after && <p className="mt-4 text-zinc-700 leading-relaxed">{s.after}</p>}
          </section>
        ))}
      </div>

      <div className="mt-16 p-6 border border-zinc-200 bg-zinc-50">
        <div className="overline text-zinc-500 mb-2">Privacy requests</div>
        <div className="font-mono text-sm">
          info@botsummits.com · +44 7915361665 · +91 8825035701
        </div>
      </div>
    </LegalLayout>
  );
}
